/**
 * Motion for the Meet Ava & Artie page.
 *
 * Reuses the home page's machinery (src/home-effects.js) for what the two pages
 * share, the WebGL light, the rim light on the characters and the magnetic
 * buttons, and adds what is new here:
 *
 *   the launch       "How we launch together", played by scrolling: the three
 *                    steps light up in turn and Artie lifts off with his rocket
 *   the guide cards  tilt toward the cursor, with a pool of light under it
 *   the capability   cards light up in their own colour under the cursor
 *
 * Like the home page, it only writes custom properties and classes. Without it
 * the page is still and complete: every step lit, Artie on the pad.
 */

import { startLight, rimLight, magnets, reduceMotion, finePointer, lite, clamp, smooth } from './home-effects.js';

export function initMeetEffects() {
  if (lite) document.documentElement.classList.add('fx-lite');
  if (reduceMotion) return;

  const hero = document.querySelector('.meet .hero');
  if (hero) {
    // The light rests between the two guides at chest height, wherever the
    // layout puts them, and the cursor can pull it across the open right side.
    const light = startLight(hero, {
      anchor: '.pair .light-anchor',
      anchorAt: [0.5, 0.5],
      rest: [0.72, 0.42],
      xClamp: [0.42, 0.97],
    });
    if (light) {
      rimLight(hero, light, '.pair .ava, .pair .artie');
      flashOnYou(hero, light);
    }
  }

  launch();
  if (finePointer) {
    guideTilt();
    capSpotlights();
    magnets();
  }
}

/**
 * Fire the shader's flash as "You grow." lands. The punch peaks halfway
 * through its 1s animation; its delay is --i * --stagger + 160ms (meet.css).
 * Timed from first paint, like the home page, not from when this module ran.
 */
function flashOnYou(hero, light) {
  const first = hero.querySelector('.kinetic .you .c');
  const title = hero.querySelector('.kinetic');
  if (!first || !title) return;
  const i = parseFloat(getComputedStyle(first).getPropertyValue('--i')) || 0;
  const stagger = parseFloat(getComputedStyle(title).getPropertyValue('--stagger')) || 24;
  const lands = i * stagger + 160 + 500;
  const fp = performance.getEntriesByType?.('paint').find((e) => e.name === 'first-paint');
  const delay = Math.max(0, (fp ? fp.startTime : performance.now()) + lands - performance.now());
  setTimeout(() => light.flash(), delay);
}

/* --------------------------------------------------------------------------- */
/*  How we launch together                                                      */
/* --------------------------------------------------------------------------- */

/**
 * Scroll-played on screens with room for it. The section is made tall and its
 * stage sticks under the header; progress through the section (0..1) lights
 * the steps in turn, fills the track down the faces, and in the last stretch
 * lifts Artie off the pad. Phones keep the plain list: a sticky stage has to
 * fit a whole screen, and three steps plus the launchpad don't at 390px.
 */
function launch() {
  const sec = document.querySelector('.meet .launch');
  if (!sec) return;
  const room = window.matchMedia('(min-width: 900px) and (min-height: 620px)');
  const steps = [...sec.querySelectorAll('.step')];
  let onScreen = false;
  let queued = false;
  let lastStep = -1;
  let wasLaunched = false;

  const update = () => {
    queued = false;
    if (!sec.classList.contains('scrub')) return;
    const r = sec.getBoundingClientRect();
    const travel = Math.max(1, r.height - window.innerHeight);
    const p = clamp(-r.top / travel, 0, 1);
    // steps at 0 / 0.28 / 0.56; liftoff over the last 30%
    const step = p < 0.28 ? 0 : p < 0.56 ? 1 : 2;
    const lift = smooth(clamp((p - 0.66) / 0.3, 0, 1));
    sec.style.setProperty('--p', clamp(p / 0.6, 0, 1).toFixed(4));
    sec.style.setProperty('--lift', lift.toFixed(4));
    if (step !== lastStep) {
      steps.forEach((s, k) => {
        s.classList.toggle('on', k <= step);
        s.classList.toggle('now', k === step);
      });
      lastStep = step;
    }
    const launched = p > 0.94;
    if (launched !== wasLaunched) sec.classList.toggle('launched', launched);
    wasLaunched = launched;
  };

  const request = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  };

  const setMode = () => {
    const on = room.matches;
    sec.classList.toggle('scrub', on);
    if (!on) {
      // back to the plain list: everything lit, Artie on the pad
      sec.style.removeProperty('--p');
      sec.style.removeProperty('--lift');
      steps.forEach((s) => s.classList.remove('on', 'now'));
      lastStep = -1;
    }
    request();
  };

  room.addEventListener?.('change', setMode);
  setMode();

  new IntersectionObserver(([e]) => {
    onScreen = e.isIntersecting;
    if (onScreen) request();
  }).observe(sec);
  window.addEventListener('scroll', () => onScreen && request(), { passive: true });
  window.addEventListener('resize', request);
}

/* --------------------------------------------------------------------------- */
/*  Cursor                                                                      */
/* --------------------------------------------------------------------------- */

/** The guide cards tip toward the cursor, with a pool of light under it. */
function guideTilt() {
  for (const card of document.querySelectorAll('.meet .guide-card')) {
    const inner = card.querySelector('.gc-inner');
    if (!inner) continue;
    card.addEventListener('pointermove', (e) => {
      const r = inner.getBoundingClientRect();
      const nx = clamp((e.clientX - r.left) / r.width, 0, 1);
      const ny = clamp((e.clientY - r.top) / r.height, 0, 1);
      inner.style.setProperty('--ry', `${((nx - 0.5) * 10).toFixed(2)}deg`);
      inner.style.setProperty('--rx', `${((0.5 - ny) * 8).toFixed(2)}deg`);
      inner.style.setProperty('--mx', `${Math.round(nx * 100)}%`);
      inner.style.setProperty('--my', `${Math.round(ny * 100)}%`);
    });
    card.addEventListener('pointerleave', () => {
      for (const k of ['--rx', '--ry', '--mx', '--my']) inner.style.removeProperty(k);
    });
  }
}

/** Each capability card lights up in its own colour under the cursor. */
function capSpotlights() {
  for (const cap of document.querySelectorAll('.meet .cap')) {
    cap.addEventListener('pointermove', (e) => {
      const r = cap.getBoundingClientRect();
      cap.style.setProperty('--mx', `${Math.round(e.clientX - r.left)}px`);
      cap.style.setProperty('--my', `${Math.round(e.clientY - r.top)}px`);
    });
  }
}
